import React, { useState, useEffect } from 'react';
import { useJobStatus, JobLog } from '../hooks/useJobStatus';

interface JobStatusModalProps {
  jobId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onComplete?: (result: any) => void;
}

export const JobStatusModal: React.FC<JobStatusModalProps> = ({
  jobId,
  isOpen,
  onClose,
  onComplete
}) => {
  const { job, loading, error, logs, cancelJob, isCompleted, isFailed, isProcessing, isPending } =
    useJobStatus(jobId);
  const [showLogs, setShowLogs] = useState(false);

  // Automatisch schließen bei Erfolg
  useEffect(() => {
    if (isCompleted && onComplete && job?.result) {
      onComplete(job.result);
      onClose();
    }
  }, [isCompleted, onComplete, job?.result, onClose]);

  // Automatisch schließen bei Fehler nach 5 Sekunden
  useEffect(() => {
    if (isFailed) {
      const timer = setTimeout(() => {
        onClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isFailed, onClose]);

  if (!isOpen || !jobId) return null;

  const jobTitle = (() => {
    const t = job?.type || '';
    switch (t) {
      case 'curricula_import':
        return 'Curricula-Import (traditionell)';
      case 'curricula_unified_import':
        return 'Curricula-Import (unified)';
      case 'classwork_submission_analysis':
        return 'Klassenarbeit · Abgabe-Analyse';
      case 'classwork_reference_analysis':
        return 'Klassenarbeit · Referenz-Analyse';
      case 'classwork_correction':
        return 'Klassenarbeit · Korrektur';
      case 'classwork_submission_ocr':
        return 'Klassenarbeit · OCR';
      case 'classwork_reference_build':
        return 'Klassenarbeit · Referenzversion';
      case 'student_file_ingest':
        return 'Schülerdatei · Import';
      case 'student_file_backfill':
        return 'Schülerdatei · Backfill';
      default:
        return t ? `Job: ${t}` : 'Hintergrund-Job';
    }
  })();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600';
      case 'failed': return 'text-red-600';
      case 'processing': return 'text-blue-600';
      case 'pending': return 'text-yellow-600';
      case 'cancelled': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Abgeschlossen';
      case 'failed': return 'Fehlgeschlagen';
      case 'processing': return 'In Bearbeitung';
      case 'pending': return 'Wartend';
      case 'cancelled': return 'Abgebrochen';
      case 'timeout': return 'Timeout';
      default: return status;
    }
  };

  const formatDuration = (start: string, end?: string) => {
    const startTime = new Date(start);
    const endTime = end ? new Date(end) : new Date();
    const duration = endTime.getTime() - startTime.getTime();
    return Math.round(duration / 1000);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {loading && !job ? 'Job' : jobTitle}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Lade Job-Status...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
              <div className="flex">
                <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Fehler</h3>
                  <p className="mt-1 text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {job && (
            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className={`font-medium ${getStatusColor(job.status)}`}>
                    {getStatusText(job.status)}
                  </span>
                  {isProcessing && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  {job.progress}%
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${
                    isCompleted ? 'bg-green-600' : 
                    isFailed ? 'bg-red-600' : 
                    'bg-blue-600'
                  }`}
                  style={{ width: `${job.progress}%` }}
                ></div>
              </div>

              {/* Duration */}
              {job.startedAt && (
                <div className="text-sm text-gray-600">
                  Dauer: {formatDuration(job.startedAt, job.completedAt)} Sekunden
                </div>
              )}

              {/* Result */}
              {isCompleted && job.result && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <h4 className="font-medium text-green-800 mb-2">Import erfolgreich abgeschlossen</h4>
                  <div className="text-sm text-green-700 space-y-1">
                    {job.result.insertedCurricula && (
                      <div>• {job.result.insertedCurricula} Curricula</div>
                    )}
                    {job.result.insertedCompetencies && (
                      <div>• {job.result.insertedCompetencies} Kompetenzen</div>
                    )}
                    {job.result.insertedContentFields && (
                      <div>• {job.result.insertedContentFields} Inhaltsfelder</div>
                    )}
                    {job.result.insertedMappings && (
                      <div>• {job.result.insertedMappings} Mappings</div>
                    )}
                  </div>
                </div>
              )}

              {/* Error */}
              {isFailed && job.error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <h4 className="font-medium text-red-800 mb-2">Import fehlgeschlagen</h4>
                  <p className="text-sm text-red-700">{job.error}</p>
                </div>
              )}

              {/* Logs */}
              {logs.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowLogs(!showLogs)}
                    className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    <svg 
                      className={`w-4 h-4 transition-transform ${showLogs ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    <span>Logs anzeigen ({logs.length})</span>
                  </button>
                  
                  {showLogs && (
                    <div className="mt-2 bg-gray-50 rounded-md p-3 max-h-40 overflow-y-auto">
                      <div className="space-y-1 text-sm font-mono">
                        {logs.map((log: JobLog, index: number) => (
                          <div key={index} className="text-gray-700">
                            <span className="text-gray-500">
                              {new Date(log.timestamp).toLocaleTimeString()}
                            </span>
                            <span className="ml-2">{log.message}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Job-ID: {jobId}
            </div>
            <div className="flex space-x-3">
              {(isProcessing || isPending) && (
                <button
                  type="button"
                  onClick={() => void cancelJob()}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200 transition-colors"
                >
                  Abbrechen
                </button>
              )}
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                {isCompleted ? 'Schließen' : 'Im Hintergrund'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
